import React, { useEffect, useMemo, useState } from 'react'
import { FilterBar, TableData } from 'components';
import { Box } from '@mui/material';
import { errorAlert } from 'helpers/alerts';
import { getDenominationBySegmentService } from 'services/admin/denominationServices';
import { getRegionsService } from 'services/admin/ladasService';
import { getOperatorsService } from 'services/admin/operatorServices';
import {
  getPDSProductsService,
  getPDSSegmentsService,
  getPINProductsService,
  getPINSegmentsService,
} from 'services/admin/productService';
import {
  getAllBagPDSProductsService,
  getAllBagPINProductsService,
  getBagProductsService,
} from 'services/client/bagService';
import { getCommercesService } from 'services/client/commerceService';
import {
  getAllProviderCredsService,
  getCredPaymentServiceProductsService,
  getCredPINProductsService,
  getTAEProductsByCredentialService,
} from 'services/admin/providerService';
import { getSegmentsByOperatorService } from 'services/admin/segmentServices';

const SELECT_ALL_ID = '__ALL__';

const ProductAssignmentTableScreen = ({ scope, type }) => {
  const [dataRows, setDataRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [loadingAssignments, setLoadingAssignments] = useState(false);
  const [credentialOptions, setCredentialOptions] = useState([]);
  const [selectedCredentialIds, setSelectedCredentialIds] = useState([]);
  const [commerceOptions, setCommerceOptions] = useState([]);
  const [selectedCommerceIds, setSelectedCommerceIds] = useState([]);
  const [credentialAssignments, setCredentialAssignments] = useState({});
  const [commerceAssignments, setCommerceAssignments] = useState({});

  const safeText = (value) => (value || '').toString().trim();

  const compareTextAsc = (a, b) =>
    safeText(a).localeCompare(safeText(b), 'es', { sensitivity: 'base' });

  const compareNumberAsc = (a, b) => {
    const numberA = Number(safeText(a).replace(/[^0-9.-]/g, ''));
    const numberB = Number(safeText(b).replace(/[^0-9.-]/g, ''));
    const validA = Number.isFinite(numberA);
    const validB = Number.isFinite(numberB);

    if (validA && validB) return numberA - numberB;
    if (validA) return -1;
    if (validB) return 1;
    return compareTextAsc(a, b);
  };

  const getProductIdFromAssignment = (item) =>
    item?.idProduct?.id || item?.idProduct?._id || item?.idProduct || item?.id;

  const selectedCredentials = useMemo(
    () => credentialOptions.filter(({ id }) => selectedCredentialIds.includes(id)),
    [credentialOptions, selectedCredentialIds]
  );

  const selectedCommerces = useMemo(
    () => commerceOptions.filter(({ id }) => selectedCommerceIds.includes(id)),
    [commerceOptions, selectedCommerceIds]
  );

  const baseColumnsCount = useMemo(() => {
    if (type === 'pds' || type === 'pin') {
      return 2;
    }

    return scope === 'comercios' ? 4 : 3;
  }, [scope, type]);

  const groupedHeaders = useMemo(() => {
    if (type !== 'tae') {
      return [];
    }

    return selectedCredentials.map(({ name }) => ({
      label: name,
      colSpan: 2,
    }));
  }, [selectedCredentials, type]);

  const dataTitle = useMemo(() => {
    if (type === 'pds' || type === 'pin') {
      return [
        "Segmento",
        "Producto",
        ...(scope === 'proveedores'
          ? selectedCredentials.map(({ name }) => name)
          : selectedCommerces.map(({ name }) => name)),
      ];
    }

    if (type === 'tae') {
      if (scope === 'comercios') {
        return [
          "Operadora",
          "Region",
          "Segmento",
          "Denominacion",
          ...selectedCommerces.map(({ name }) => name),
        ];
      }

      return [
        "Operadora",
        "Segmento",
        "Denominacion",
        ...selectedCredentials.flatMap(() => ["Check", "Carriers"]),
      ];
    }

    if (scope === 'comercios') {
      return ["Operadora", "Region", "Segmento", "Denominacion", ...selectedCommerces.map(({ name }) => name)];
    }

    return ["Operadora", "Segmento", "Denominacion"];
  }, [scope, type, selectedCredentials, selectedCommerces]);

  useEffect(() => {
    const loadRechargeCredentials = async () => {
      if (scope !== 'proveedores') {
        setCredentialOptions([]);
        setSelectedCredentialIds([]);
        setCredentialAssignments({});
        return;
      }

      try {
        const providers = await getAllProviderCredsService();
        setCredentialOptions((providers || []).filter((item) => item.provider?.type === (type === 'tae' ? 'RECHARGE' : 'SERVICE')).map((credential) => ({
          id: credential.id,
          name: credential.name_connector,
        })).sort((a, b) => compareTextAsc(a.name, b.name)));
      } catch (error) {
        errorAlert(error);
      }
    };

    loadRechargeCredentials();
  }, [scope]);

  useEffect(() => {
    const loadCommerces = async () => {
      if (scope !== 'comercios') {
        setCommerceOptions([]);
        setSelectedCommerceIds([]);
        return;
      }

      try {
        const commerces = await getCommercesService(undefined, true);
        setCommerceOptions((commerces || [])
          .map(({ _id: id, username: name, bags = [] }) => ({
            id,
            name,
            bagByType: bags.reduce((acc, bag) => ({
              ...acc,
              [bag.type]: bag.id,
            }), {}),
          }))
          .sort((a, b) => compareTextAsc(a.name, b.name)));
      } catch (error) {
        errorAlert(error);
      }
    };

    loadCommerces();
  }, [scope]);

  useEffect(() => {
    setSelectedCredentialIds((previous) => previous.filter((id) => credentialOptions.some((option) => option.id === id)));
  }, [credentialOptions]);

  useEffect(() => {
    setSelectedCommerceIds((previous) => previous.filter((id) => commerceOptions.some((option) => option.id === id)));
  }, [commerceOptions]);

  useEffect(() => {
    const loadTAERows = async () => {
      const operators = await getOperatorsService();
      const activeOperators = (operators || []).filter(({ state = true }) => state);

      let regions = [];
      if (scope === 'comercios') {
        regions = await getRegionsService();
        regions = (regions || []).filter(({ state = true }) => state);
      }

      const rowsByOperator = await Promise.all(
        activeOperators.map(async (operator) => {
          const segments = await getSegmentsByOperatorService(operator.id);
          const activeSegments = (segments || []).filter(({ state = true }) => state);

          const rowsBySegment = await Promise.all(
            activeSegments.map(async (segment) => {
              const denominations = await getDenominationBySegmentService(segment.id);
              const activeDenominations = (denominations || []).filter(({ state = true }) => state);

              if (scope === 'comercios') {
                if (operator.regions && regions.length > 0) {
                  return regions.flatMap((region) =>
                    activeDenominations.map((denomination) => ({
                      productId: denomination.id,
                      operatorName: operator.name,
                      operatorOrder: operator.order,
                      regionName: region.nombre,
                      segmentName: segment.name,
                      denominationName: denomination.denomination,
                    }))
                  );
                }

                return activeDenominations.map((denomination) => ({
                  productId: denomination.id,
                  operatorName: operator.name,
                  operatorOrder: operator.order,
                  regionName: '-',
                  segmentName: segment.name,
                  denominationName: denomination.denomination,
                }));
              }

              return activeDenominations.map((denomination) => ({
                productId: denomination.id,
                operatorName: operator.name,
                operatorOrder: operator.order,
                segmentName: segment.name,
                denominationName: denomination.denomination,
              }));
            })
          );

          return rowsBySegment.flat();
        })
      );

      return rowsByOperator.flat().sort((a, b) => {
        const operatorOrderA = Number(a.operatorOrder);
        const operatorOrderB = Number(b.operatorOrder);
        const hasOperatorOrderA = Number.isFinite(operatorOrderA);
        const hasOperatorOrderB = Number.isFinite(operatorOrderB);

        if (hasOperatorOrderA && hasOperatorOrderB && operatorOrderA !== operatorOrderB) {
          return operatorOrderA - operatorOrderB;
        }

        if (hasOperatorOrderA !== hasOperatorOrderB) {
          return hasOperatorOrderA ? -1 : 1;
        }

        const byOperator = compareTextAsc(a.operatorName, b.operatorName);
        if (byOperator !== 0) return byOperator;

        const byRegion = compareTextAsc(a.regionName, b.regionName);
        if (byRegion !== 0) return byRegion;

        const bySegment = compareTextAsc(a.segmentName, b.segmentName);
        if (bySegment !== 0) return bySegment;

        return compareNumberAsc(a.denominationName, b.denominationName);
      });
    };

    const loadSegmentProductRows = async (currentType) => {
      const getSegmentsService = currentType === 'pds' ? getPDSSegmentsService : getPINSegmentsService;
      const getProductsService = currentType === 'pds' ? getPDSProductsService : getPINProductsService;

      const segments = await getSegmentsService();
      const activeSegments = (segments || []).filter(({ state = true }) => state);

      const rowsBySegment = await Promise.all(
        activeSegments.map(async (segment) => {
          const products = await getProductsService(segment.id);
          const activeProducts = (products || []).filter(({ state = true }) => state);

          return activeProducts.map((product) => ({
            productId: product.id,
            segmentName: segment.name,
            productName: product.name,
          }));
        })
      );

      return rowsBySegment.flat().sort((a, b) => {
        const bySegment = compareTextAsc(a.segmentName, b.segmentName);
        if (bySegment !== 0) return bySegment;
        return compareTextAsc(a.productName, b.productName);
      });
    };

    const loadRows = async () => {
      setLoadingRows(true);
      try {
        if (type === 'tae') {
          setDataRows(await loadTAERows());
        } else if (type === 'pds' || type === 'pin') {
          setDataRows(await loadSegmentProductRows(type));
        } else {
          setDataRows([]);
        }
      } catch (error) {
        errorAlert(error);
      } finally {
        setLoadingRows(false);
      }
    };

    loadRows();
  }, [scope, type]);

  useEffect(() => {
    const loadCommerceAssignments = async () => {
      if (scope !== 'comercios' || selectedCommerceIds.length === 0) {
        setCommerceAssignments({});
        return;
      }

      setLoadingAssignments(true);
      try {
        const assignments = {};

        for (const commerceId of selectedCommerceIds) {
          const selectedCommerce = commerceOptions.find((commerce) => commerce.id === commerceId);
          const bagId = selectedCommerce?.bagByType?.[type?.toUpperCase()];
          const assignedProductIds = new Set();

          if (!bagId) {
            assignments[commerceId] = { ids: assignedProductIds };
            continue;
          }

          if (type === 'tae') {
            const products = await getBagProductsService(bagId, 'denominaciones');
            (products || []).forEach((product) => {
              const productId = getProductIdFromAssignment(product);
              if (productId) assignedProductIds.add(productId);
            });
          }

          if (type === 'pds') {
            const products = await getAllBagPDSProductsService(bagId);
            (products || []).forEach((product) => {
              const productId = getProductIdFromAssignment(product);
              const isAssigned = product?.state === undefined ? true : !!product.state;
              if (productId && isAssigned) assignedProductIds.add(productId);
            });
          }

          if (type === 'pin') {
            const products = await getAllBagPINProductsService(bagId);
            (products || []).forEach((product) => {
              const productId = getProductIdFromAssignment(product);
              const isAssigned = product?.state === undefined ? true : !!product.state;
              if (productId && isAssigned) assignedProductIds.add(productId);
            });
          }

          assignments[commerceId] = { ids: assignedProductIds };
        }

        setCommerceAssignments(assignments);
      } catch (error) {
        errorAlert(error);
      } finally {
        setLoadingAssignments(false);
      }
    };

    loadCommerceAssignments();
  }, [scope, type, selectedCommerceIds, commerceOptions]);

  useEffect(() => {
    const loadAssignments = async () => {
      if (scope !== 'proveedores' || selectedCredentialIds.length === 0) {
        setCredentialAssignments({});
        return;
      }

      setLoadingAssignments(true);
      try {
        const assignments = {};

        for (const credentialId of selectedCredentialIds) {
          const assignedProductIds = new Set();
          const assignedProductCarriers = {};

          if (type === 'tae') {
            const products = await getTAEProductsByCredentialService(credentialId);
            (products || []).forEach((product) => {
              const productId = getProductIdFromAssignment(product);
              if (productId) {
                assignedProductIds.add(productId);
                assignedProductCarriers[productId] = product?.amounts?.map(amount => amount?.carrier_id).sort((a,b) => a - b).filter(Boolean) || [];
              }
            });
          }

          if (type === 'pds') {
            const products = await getCredPaymentServiceProductsService(credentialId);
            (products || []).forEach((product) => {
              const productId = getProductIdFromAssignment(product);
              const isAssigned = product?.state === undefined ? true : !!product.state;
              if (productId && isAssigned) assignedProductIds.add(productId);
            });
          }

          if (type === 'pin') {
            const products = await getCredPINProductsService(credentialId);
            (products || []).forEach((product) => {
              const productId = getProductIdFromAssignment(product);
              const isAssigned = product?.state === undefined ? true : !!product.state;
              if (productId && isAssigned) assignedProductIds.add(productId);
            });
          }

          assignments[credentialId] = {
            ids: assignedProductIds,
            carriers: assignedProductCarriers,
          };
        }

        setCredentialAssignments(assignments);
      } catch (error) {
        errorAlert(error);
      } finally {
        setLoadingAssignments(false);
      }
    };

    loadAssignments();
  }, [scope, type, selectedCredentialIds]);

  return (
    <>
      {
        scope === 'proveedores' && credentialOptions.length > 0 && (
          <FilterBar
            showTitle={false}
            filters={[
              {
                name: 'credentials',
                label: 'Proveedores',
                type: 'select',
                multiple: true,
                data: [{ id: SELECT_ALL_ID, name: 'Seleccionar todos' }, ...credentialOptions],
                render: (value = []) => value
                  .map((id) => credentialOptions.find((item) => item.id === id)?.name)
                  .filter(Boolean)
                  .join(', '),
              },
            ]}
            defaultValues={{ credentials: selectedCredentialIds }}
            handlerFilter={(fields) => {
              const selectedIds = fields?.credentials || [];
              if (selectedIds.includes(SELECT_ALL_ID)) {
                setSelectedCredentialIds(credentialOptions.map(({ id }) => id));
                return;
              }

              setSelectedCredentialIds(selectedIds);
            }}
          />
        )
      }
      {
        scope === 'comercios' && commerceOptions.length > 0 && (
          <FilterBar
            showTitle={false}
            filters={[
              {
                name: 'commerces',
                label: 'Comercios',
                type: 'select',
                multiple: true,
                data: [{ id: SELECT_ALL_ID, name: 'Seleccionar todos' }, ...commerceOptions],
                render: (value = []) => value
                  .map((id) => commerceOptions.find((item) => item.id === id)?.name)
                  .filter(Boolean)
                  .join(', '),
              },
            ]}
            defaultValues={{ commerces: selectedCommerceIds }}
            handlerFilter={(fields) => {
              const selectedIds = fields?.commerces || [];
              if (selectedIds.includes(SELECT_ALL_ID)) {
                setSelectedCommerceIds(commerceOptions.map(({ id }) => id));
                return;
              }

              setSelectedCommerceIds(selectedIds);
            }}
          />
        )
      }
      <Box mt={2}>
        <TableData
          dataTitle={dataTitle}
          dataRows={dataRows}
          loading={loadingRows || loadingAssignments}
          headerGroups={groupedHeaders}
          headerGroupOffset={baseColumnsCount}
          rowsPerPageOpt={1000}
          formatter={({ productId, operatorName, regionName, segmentName, denominationName, productName }) => {
            if (type === 'pds' || type === 'pin') {
              const selectedIds = scope === 'proveedores' ? selectedCredentialIds : selectedCommerceIds;
              const assignments = scope === 'proveedores' ? credentialAssignments : commerceAssignments;

              return [
                segmentName,
                productName,
                ...selectedIds.map((itemId) => ({
                  type: 'boolean',
                  name: `${scope}_${itemId}`,
                  value: !!assignments[itemId]?.ids?.has(productId),
                  disabled: true,
                  uncheckedGray: true,
                })),
              ];
            }

            if (type === 'tae') {
              const baseColumns = scope === 'comercios'
                ? [operatorName, regionName, segmentName, denominationName]
                : [operatorName, segmentName, denominationName];

              if (scope === 'comercios') {
                return [
                  ...baseColumns,
                  ...selectedCommerceIds.map((commerceId) => ({
                    type: 'boolean',
                    name: `commerce_${commerceId}_check`,
                    value: !!commerceAssignments[commerceId]?.ids?.has(productId),
                    disabled: true,
                    uncheckedGray: true,
                  })),
                ];
              }

              const credentialColumns = selectedCredentialIds.flatMap((credentialId) => ([
                {
                  type: 'boolean',
                  name: `cred_${credentialId}_check`,
                  value: !!credentialAssignments[credentialId]?.ids?.has(productId),
                  disabled: true,
                  uncheckedGray: true,
                },
                {
                  type: 'text',
                  name: `cred_${credentialId}_carriers`,
                  value: (credentialAssignments[credentialId]?.carriers?.[productId] || []).join(', ') || '-',
                },
              ]));

              return [...baseColumns, ...credentialColumns];
            }

            return scope === 'comercios'
              ? [operatorName, regionName, segmentName, denominationName]
              : [operatorName, segmentName, denominationName];
          }}
          search={false}
          pagination={false}
        />
      </Box>
    </>
  )
}

export default ProductAssignmentTableScreen